import { FC, JSX, useEffect, useState } from "react";
import "./Payments.scss";
import MasterPage from "../../MasterPage/MasterPage";
import PageBase from "../../../components/PageBase/PageBase";
import SkeletonTable from "../../../components/SkeletonTable/SkeletonTable";
import AppPagination from "../../../components/AppPagination/AppPagination";
import { usePaginate } from "../../../providers/PaginateProvider";
import { usePayments } from "../../../providers/Financial/Payments/PaymentsProvider";
import { useSidebarFilter } from "../../../providers/SidebarFilterProvider";
import SidebarPaymentsFilter from "../../../components/Financial/Payments/SidebarPaymentsFilter/SidebarPaymentsFilter";
import ModalPaymentView from "../../../components/Financial/Payments/ModalPaymentView/ModalPaymentView";
import DropdownMenuTable from "../../../components/DropdownMenuTable/DropdownMenuTable";
import { Dropdown } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEye } from "@fortawesome/free-solid-svg-icons";
import moment from "moment";
import FormatMoney from "../../../utils/FormatMoney";
import RenderMethodIcon from "../../../utils/RenderMethodIcon";

interface PaymentsProps { }

const Payments: FC<PaymentsProps> = () => {
  const {
    pages,
    payments,
    params,
    setParams,
    isLoading,
    error,
    methodsMap,
    statusesMap,
    subscriptionsMap,
  } = usePayments();
  const { handlePaginate } = usePaginate();
  const { showSidebarFilter, setShowSidebarFilter } = useSidebarFilter();
  const [showModalView, setShowModalView] = useState<boolean>(false);
  const [selectedPayment, setSelectedPayment] = useState<any>(null);

  const handleView = (payment: any) => {
    setSelectedPayment(payment);
    setShowModalView(true);
  };

  const handleCloseModal = () => {
    setSelectedPayment(null);
    setShowModalView(false);
  };


  const renderList = (): JSX.Element => {
    return (
      <>
        {payments?.length > 0 ? (
          <>
            {payments.map((x: any) => {
              const subscription = x?.subscription
                || x?.plan_subscription
                || subscriptionsMap[String(x?.plan_subscription_id)]
                || null;

              const methodName = x?.plans_payments_methods?.name
                || x?.method?.name
                || methodsMap[String(x?.plans_payments_methods?.id ?? x?.plan_payment_method_id)]
                || null;
              const statusName = x?.plans_payments_status?.name
                || x?.status?.name
                || statusesMap[String(x?.plans_payments_status?.id ?? x?.plan_payment_status_id)]
                || null;
              const user = subscription?.user || null;
              const plan = subscription?.plan || null;
              const cycle = subscription?.cycle || null;

              return (
                <tr key={x.id} className="text-sm">
                  <td className="text-center">{x.id}</td>
                  <td className="text-center">
                    {x?.payment_date
                      ? moment(x.payment_date).format("DD/MM/YYYY HH:mm")
                      : "-"}
                  </td>
                  <td>
                    <b>{x?.amount_paid != null ? FormatMoney(x.amount_paid) : "-"}</b>
                  </td>
                  <td>
                    {methodName ? RenderMethodIcon(methodName) : "-"}
                  </td>
                  <td>{plan?.name || "-"}</td>
                  <td className="text-center">
                    {cycle ? (cycle === "annual" ? "Anual" : "Mensal") : "-"}
                  </td>
                  <td>
                    {user ? (
                      <>
                        {user.partner_code
                          ? <><strong>{user.partner_code}</strong> - </>
                          : ""}
                        {user.name || "-"}
                      </>
                    ) : "-"}
                  </td>
                  <td className="text-center">
                    {statusName ? (
                      <span
                        className={`badge ${statusName.toLowerCase() === "pago"
                          ? "bg-success"
                          : statusName.toLowerCase() === "pendente"
                            ? "bg-warning"
                            : "bg-danger"
                          }`}
                      >
                        {statusName}
                      </span>
                    ) : "-"}
                  </td>
                  <td>{x?.stripe_payment_intent_id}</td>
                  <td className="text-center">
                    <DropdownMenuTable key={x.id}>
                      <Dropdown.Item
                        className="border-radius-md py-2"
                        onClick={() => handleView({ ...x, methodName, statusName, subscription })}
                      >
                        <FontAwesomeIcon icon={faEye} className="me-2" /> Visualizar
                      </Dropdown.Item>
                    </DropdownMenuTable>
                  </td>
                </tr>
              );
            })}
          </>
        ) : (
          <tr>
            <td colSpan={9} className="text-center text-sm">
              Não há registros
            </td>
          </tr>
        )}
      </>
    );
  };

  const renderContent = (): JSX.Element => {
    return (
      <>
        {!isLoading ? (
          <div>
            <div className="table-responsive">
              <table className="table table-flush m-0 w-100">
                <thead className="thead-light">
                  <tr>
                    <th className="text-uppercase opacity-7 text-center" scope="col">
                      #
                    </th>
                    <th className="text-uppercase opacity-7 text-center" scope="col">
                      Data do Pagamento
                    </th>
                    <th className="text-uppercase opacity-7" scope="col">
                      Valor
                    </th>
                    <th className="text-uppercase opacity-7" scope="col">
                      Método
                    </th>
                    <th className="text-uppercase opacity-7" scope="col">
                      Plano
                    </th>
                    <th className="text-uppercase opacity-7 text-center" scope="col">
                      Tipo
                    </th>
                    <th className="text-uppercase opacity-7" scope="col">
                      Usuário
                    </th>
                    <th className="text-uppercase opacity-7 text-center" scope="col">
                      Status
                    </th>
                    <th
                      className="text-uppercase opacity-7"
                      scope="col"
                    >
                      Id Stripe
                    </th>
                    <th className="text-uppercase opacity-7 text-center" scope="col">
                      Ações
                    </th>
                  </tr>
                </thead>
                <tbody>{renderList()}</tbody>
              </table>
            </div>

            {payments?.length > 0 && (
              <div>
                <nav aria-label="...">
                  <AppPagination
                    pages={pages}
                    handlePaginate={handlePaginate}
                    params={params}
                    setParams={setParams}
                    style={{ backgroundColor: "#fff" }}
                  />
                </nav>
              </div>
            )}
          </div>
        ) : (
          <SkeletonTable />
        )}
      </>
    );
  };

  useEffect(() => {
    setParams({ ...params, page: 1, withPaginate: true, filter: {} });
  }, []);

  return (
    <MasterPage title="Pagamentos" icon="fas fa-credit-card" footerFixed>
      <div className="Payments" data-testid="Payments">
        <PageBase
          title="Pagamentos"
          subtitle="Histórico de pagamentos das assinaturas"
          handleFilter={() =>
            !!showSidebarFilter
              ? setShowSidebarFilter(false)
              : setShowSidebarFilter(true)
          }
          error={error}
          children={renderContent()}
          hasFilter
        />
      </div>

      <SidebarPaymentsFilter />

      <ModalPaymentView
        show={showModalView}
        onClose={handleCloseModal}
        payment={selectedPayment}
      />
    </MasterPage>
  );
};

export default Payments;
